import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPendingDowngrade, clearPendingDowngrade, PendingDowngrade } from '../services/pendingDowngradeService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * @fileoverview Hook para detectar y manejar downgrades pendientes
 * Se ejecuta al cargar la app y verifica si el usuario tiene un downgrade pendiente
 */

export function usePendingDowngrade() {
  const { user } = useAuth();
  const [pendingDowngrade, setPendingDowngrade] = useState<PendingDowngrade | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const inFlightRef = useRef<Promise<void> | null>(null);
  const cancelledRef = useRef(false);
  
  // Verificar si hay pending downgrade al cargar
  useEffect(() => {
    cancelledRef.current = false;
    void checkPendingDowngrade();
    return () => {
      cancelledRef.current = true;
    };
  }, [user?.uid]);
  
  const checkPendingDowngrade = async () => {
    if (!user?.uid) {
      setIsChecking(false);
      return;
    }

    // Dedupe: nunca correr más de una verificación en paralelo.
    if (inFlightRef.current) {
      return;
    }

    // Throttle: evitar loops por re-mount / re-auth / refresh.
    // Usamos localStorage para que aplique a web y a native (Capacitor WebView).
    const throttleKey = `pending_downgrade_check_${user.uid}`;
    const nowMs = Date.now();
    try {
      const lastMs = Number(localStorage.getItem(throttleKey) || '0');
      // 60s es suficiente para cortar refresh-loop sin perder reactividad.
      if (nowMs - lastMs < 60_000) {
        setIsChecking(false);
        return;
      }
      localStorage.setItem(throttleKey, String(nowMs));
    } catch {
      // Si localStorage no está disponible (p.ej. restricciones del browser), no bloquear la verificación.
    }
    
    // NO mostrar el wizard en páginas públicas o admin
    const currentPath = window.location.pathname;
    const publicPaths = [
      '/signin',
      '/sign-in',
      '/login',
      '/signup',
      '/sign-up',
      '/register',
      '/reset-password',
      '/forgot-password',
      '/admin',
      '/onboarding',
      '/verify',
      '/auth',
    ];
    
    const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));
    
    if (isPublicPage) {
      console.log('🔒 No mostrar wizard en página pública/admin:', currentPath);
      setIsChecking(false);
      return;
    }

    const run = (async () => {
      setIsChecking(true);

      // Timeout hard: si Firestore se queda colgado por red, no bloquear UX.
      const timeoutMs = 12_000;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const timeoutPromise = new Promise<'__timeout__'>((resolve) => {
        timeoutId = setTimeout(() => resolve('__timeout__'), timeoutMs);
      });

      try {
        const pendingOrTimeout = await Promise.race([
          getPendingDowngrade(user.uid) as Promise<PendingDowngrade | null>,
          timeoutPromise,
        ]);

        if (pendingOrTimeout === '__timeout__') {
          return;
        }

        const pending = pendingOrTimeout;
      
      if (pending) {
        console.log('⚠️ Pending downgrade detectado:', pending);
        
        // ⭐ VERIFICAR SI ES OBSOLETO: Usuario ya renovó su plan
        // Obtener plan actual del usuario
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const currentPlan = userData.subscription?.plan || 'free';
          
          // Orden de planes: free < familiar < premium
          const planOrder: Record<string, number> = {
            'free': 0,
            'familiar': 1,
            'premium': 2
          };
          
          const currentLevel = planOrder[currentPlan] || 0;
          const targetLevel = planOrder[pending.toPlanId] || 0;
          
          // ⭐ CORREGIDO: Si el plan actual es MENOR que el toPlan, significa que ya hizo upgrade
          // Ejemplo: currentPlan="familiar", toPlan="free" → Es VÁLIDO (downgrade pendiente)
          // Ejemplo: currentPlan="free", toPlan="familiar" → Es OBSOLETO (ya pagó)
          if (currentLevel < targetLevel) {
            console.log(`🔄 Pending downgrade obsoleto: plan actual (${currentPlan}) < target (${pending.toPlanId}) - usuario ya hizo upgrade`);
            console.log('🧹 Limpiando pending downgrade obsoleto...');
            await clearPendingDowngrade(user.uid);
            setPendingDowngrade(null);
            setWizardOpen(false);
            return;
          }
          
          // Si llegamos aquí, el pending_downgrade es VÁLIDO
          console.log(`✅ Pending downgrade VÁLIDO: plan actual (${currentPlan}) >= target (${pending.toPlanId})`);
          console.log(`📋 Mostrando wizard de downgrade...`);
        }
        
        // Pending downgrade es válido, mostrar wizard
        setPendingDowngrade(pending);
        setWizardOpen(true);
      }
    } catch (error) {
      console.error('Error verificando pending downgrade:', error);
    } finally {
      if (timeoutId) {
        try {
          clearTimeout(timeoutId);
        } catch {
        }
      }
      if (!cancelledRef.current) {
        setIsChecking(false);
      }
    }
    })();

    inFlightRef.current = run;
    try {
      await run;
    } finally {
      inFlightRef.current = null;
    }
  };
  
  // Callback para cuando el usuario completa el wizard
  const onWizardComplete = async () => {
    if (!user?.uid) return;
    
    try {
      // Limpiar pending downgrade
      await clearPendingDowngrade(user.uid);
      
      setPendingDowngrade(null);
      setWizardOpen(false);
    } catch (error) {
      console.error('Error completando downgrade:', error);
    }
  };
  
  // Callback para cuando el usuario cancela (aunque no debería poder)
  const onWizardCancel = () => {
    // No permitir cancelar - es obligatorio
    alert('Debes completar el proceso de cambio de plan para continuar usando la aplicación.');
  };
  
  return {
    pendingDowngrade,
    isChecking,
    wizardOpen,
    setWizardOpen,
    onWizardComplete,
    onWizardCancel,
    checkPendingDowngrade,
  };
}
